import React from 'react';
import { reduxForm, Field } from 'redux-form';
import PropTypes from 'prop-types';
import { Button, Checkbox } from 'semantic-ui-react';
import _ from 'lodash';

import { NotificationDialogManager } from 'Components/';
import withKeyboard from 'Common/Components/Keyboard/withKeyboard';
import withReduxFormBlur from 'Common/Components/Keyboard/withReduxFormBlur';
import { DOMHelpers } from 'Common/Helpers';
import BaseForm from '../../../components/forms/BaseForm';
import { FieldEnum } from '../../../common/FieldEnum';
import {
  renderLargeField,
  FieldWithExtractedReduxActions,
} from '../../../common/FormHelper';
import { StyledReduxFormError } from '../../../components';
import { LOGIN_FORM_NAME } from '../../../common/FormEnum';
import { RESET_PASSWORD_STEP } from '../helpers/constants';

const LargeFieldWithKeyboard = FieldWithExtractedReduxActions(
  withReduxFormBlur(withKeyboard(renderLargeField))
);

class LoginForm extends BaseForm {
  static propTypes = {
    hideForgotPassword: PropTypes.bool,
    hideSettings: PropTypes.bool,
    loginButtonText: PropTypes.string,
    disableDistrict: PropTypes.bool,
    leftButton: PropTypes.node,
  };

  static defaultProps = {
    hideForgotPassword: false,
    hideSettings: false,
    loginButtonText: 'Login',
    disableDistrict: false,
    leftButton: null,
  };

  constructor(props) {
    super(props);

    this.state = { settingsDisplay: false };
    this.inputRefs = [React.createRef(), React.createRef(), React.createRef()];
    this.activeInputIndex = 0;
    this.loginSettingsRef = React.createRef();
  }

  settings() {
    const { ui } = this.props;
    return (
      <div ref={this.loginSettingsRef} className="login--settings">
        <div className="settings--keyboard">
          <Checkbox
            label="Display Touch Screen Keyboard?"
            checked={ui.keyboardActive}
            onChange={(e, data) => {
              this.keyboardUseToggle();
            }}
          />
        </div>
      </div>
    );
  }

  onKeyboardInputFocus = index => e => {
    this.activeInputIndex = index;
  };

  onKeyboardInputTab = () => {
    const nextInputIndex = this.activeInputIndex + 1;

    if (nextInputIndex !== this.inputRefs.length) {
      const nextInput = this.inputRefs[nextInputIndex].current.wrappedInstance;
      nextInput.focus();
    }
  };

  render() {
    const {
      handleSubmit,
      submitting,
      invalid,
      error,
      offline,
      hideForgotPassword,
      hideSettings,
      disableDistrict,
      leftButton,
    } = this.props;
    const { settingsDisplay } = this.state;

    return (
      <div>
        <form onSubmit={handleSubmit}>
          <div>
            <Field
              disabled={disableDistrict}
              maxLength={25}
              name={FieldEnum.DistrictId}
              component={LargeFieldWithKeyboard}
              label="District Code"
              attr={{
                autoComplete: 'new-password',
              }}
              forwardRef={this.inputRefs[0]}
              onFocus={this.onKeyboardInputFocus(0)}
              onTabPress={this.onKeyboardInputTab}
              usePortal
              useMargin
            />
          </div>

          <div>
            <Field
              name={FieldEnum.Username}
              component={LargeFieldWithKeyboard}
              maxLength={10}
              type="text"
              label="Username"
              attr={{
                autoComplete: 'new-password',
              }}
              forwardRef={this.inputRefs[1]}
              onFocus={this.onKeyboardInputFocus(1)}
              onTabPress={this.onKeyboardInputTab}
              usePortal
              useMargin
            />
          </div>

          <div style={{ marginBottom: '5px' }}>
            <Field
              name={FieldEnum.Password}
              component={LargeFieldWithKeyboard}
              type="password"
              label="Password"
              attr={{
                autoComplete: 'new-password',
              }}
              padBottom={false}
              forwardRef={this.inputRefs[2]}
              onFocus={this.onKeyboardInputFocus(2)}
              onTabPress={this.onKeyboardInputTab}
              usePortal
              useMargin
            />
          </div>

          <div className="login--forgot-password-link">
            {!hideForgotPassword && (
              <a
                href=""
                onClick={e => {
                  e.preventDefault();
                  this.props.setResetPasswordStep(
                    RESET_PASSWORD_STEP.Step_1_Before_Email
                  );
                }}
              >
                Forgot Password?
              </a>
            )}
          </div>

          <StyledReduxFormError error={error} />
          <div className="login--buttons">
            {leftButton}
            {!hideSettings && (
              <Button
                primary
                floated="left"
                type="button"
                icon="settings"
                size="tiny"
                className="login--settings-trigger"
                onClick={event => {
                  event.preventDefault();
                  this.setState(
                    {
                      settingsDisplay: !this.state.settingsDisplay,
                    },
                    () => {
                      const loginSettings = this.loginSettingsRef.current;
                      if (loginSettings) {
                        const clientRect = loginSettings.getBoundingClientRect();
                        const isInViewport = DOMHelpers.isInViewport(
                          loginSettings
                        );

                        if (!isInViewport) {
                          // Keyboard toggle field is cutoff so scroll it into view
                          window.scrollBy(0, clientRect.height);
                        }
                      }
                    }
                  );
                }}
              />
            )}
            <Button
              primary
              type="submit"
              floated="right"
              disabled={submitting || invalid}
              loading={this.props.isSubmitting}
            >
              {offline && 'Offline'} {this.props.loginButtonText}
            </Button>
          </div>
          {!settingsDisplay ? null : this.settings()}
          <NotificationDialogManager />
        </form>
      </div>
    );
  }
}

const validate = values => {
  const errors = {};

  if (
    !values[FieldEnum.DistrictId] ||
    _.trim(values[FieldEnum.DistrictId]).length < 1
  ) {
    errors[FieldEnum.DistrictId] = 'District Code Required';
  }

  if (
    !values[FieldEnum.Username] ||
    _.trim(values[FieldEnum.Username]).length < 1
  ) {
    errors[FieldEnum.Username] = 'Username Required';
  }

  if (values[FieldEnum.Username] && values[FieldEnum.Username].length > 10) {
    errors[FieldEnum.Username] = 'Username must not exceed 10 characters.';
  }

  if (!values[FieldEnum.Password]) {
    errors[FieldEnum.Password] = 'Password Required';
  }

  return errors;
};

export default reduxForm({
  validate,
  form: LOGIN_FORM_NAME,
})(LoginForm);



// WEBPACK FOOTER //
// ./src/pages/login/components/LoginForm.js